import {
    ButtonFlavor,
    ButtonSize,
    ButtonType,
    ExButton,
    ExIcon,
    ExIconButton,
    ExTooltip,
    IconButtonFlavor,
    IconButtonSize,
    IconButtonType,
    TooltipAlignment,
    TooltipPosition,
} from '@boomi/exosphere';
import { useCallback, useEffect, useState } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { type AutomateError, getAuthenticationStatus } from '../api';
import { useApp } from '../common/AppProvider';
import IconCircle from '../common/IconCircle';
import TaskItemPicker from '../common/TaskItemPicker';
import { translations } from '../translations/translations';
import type { Action, AuthenticationStatusResponse, Connector, Trigger } from '../types/types';
import { getAuthenticatedConnectors } from '../utils/utils';
import Loading from '../Loading';

import './new-task-builder.css';

const NewTaskBuilder = () => {
    const { language, connectors, alertError } = useApp();
    const navigate = useNavigate();
    // Auth. status
    const [authenticationStatus, setAuthenticationStatus] = useState<AuthenticationStatusResponse>(
        {},
    );
    const [isLoadingAuthenticationStatus, setIsLoadingAuthenticationStatus] = useState(true);
    // New task triggers and actions
    const [triggerConnector, setTriggerConnector] = useState<Connector | undefined>(undefined);
    const [actionConnectors, setActionConnectors] = useState<Connector[]>([]);
    const [taskTrigger, setTaskTrigger] = useState<Trigger | null>(null);
    const [taskActions, setTaskActions] = useState<Action[]>([]);
    // Wizard
    const wizardSteps = [
        {
            title: translations[language].TASK_BUILDER_step_1_title,
            isDone: !!triggerConnector, // step is done when this is not null/undefined/empty
        },
        {
            title: translations[language].TASK_BUILDER_step_2_title,
            isDone: !!taskTrigger,
        },
        {
            title: translations[language].TASK_BUILDER_step_3_title,
            isDone: !!actionConnectors.length,
        },
        {
            title: translations[language].TASK_BUILDER_step_4_title,
            isDone: !!taskActions.length,
        },
    ];
    const [wizardStep, setWizardStep] = useState<number>(0);
    const wizardLastStep = wizardSteps.length - 1;

    // Getting all authentication statuses for all connectors.
    const loadAuthenticationStatus = useCallback(async () => {
        try {
            setIsLoadingAuthenticationStatus(true);
            const status = await getAuthenticationStatus();
            setAuthenticationStatus(status);
        } catch (error) {
            alertError({
                message: translations[language].ERROR_getting_authentication_status,
                traceId: (error as AutomateError).json?.traceId,
            });
        } finally {
            setIsLoadingAuthenticationStatus(false);
        }
    }, [language, alertError]);

    useEffect(() => {
        loadAuthenticationStatus();
    }, [loadAuthenticationStatus]);

    const onActionConnectorSelected = (selected: Connector) => {
        const connectorAlreadySelected = actionConnectors.includes(selected);
        const updatedConnectors = connectorAlreadySelected
            ? actionConnectors.filter((connector) => connector.id !== selected.id)
            : [...actionConnectors, selected];
        const connectorWasRemoved = actionConnectors.length > updatedConnectors.length;

        setActionConnectors(updatedConnectors);

        // Make sure to remove any actions from taskActions it their connector is no longer
        // selected and in the actionConnectors.
        if (connectorWasRemoved) {
            const filteredActions = taskActions.filter((item) =>
                updatedConnectors.some((filterItem) => filterItem.id === item.providerId),
            );

            setTaskActions(filteredActions);
        }
    };

    const onActionSelected = (selected: Action) => {
        const actionAlreadySelected = taskActions.includes(selected);
        const updatedActions = actionAlreadySelected
            ? taskActions.filter((action) => action.id !== selected.id)
            : [...taskActions, selected];

        setTaskActions(updatedActions);
    };

    // Generate the URL with params that will pass selected triggers + actions to the task editor.
    const onCreateTaskFromSelection = (trigger: Trigger, actions: Action[]) => {
        const params = new URLSearchParams();

        // add trigger
        params.append('trigger-connector', trigger.providerId);
        params.append('trigger-event', trigger.id);

        // add action(s)
        actions.forEach((action) => {
            params.append('action-connector', action.providerId);
            params.append('action-event', action.id);
        });

        navigate(`/tasks/new-task/?${params}`);
    };

    const renderSteps = () => {
        switch (wizardStep) {
            // Start at step 2 (index 1) because the default case will also be the step 1 (index 0).
            case 1:
                return (
                    <TaskItemPicker
                        key={wizardSteps[wizardStep].title}
                        connectors={connectors}
                        pickerItems={
                            triggerConnector ? Object.values(triggerConnector?.triggers) : []
                        }
                        isMultiSelect={false}
                        selectedItems={taskTrigger ? [taskTrigger] : []}
                        onSelect={(selected) => setTaskTrigger(selected as Trigger)}
                        language={language}
                    />
                );
            case 2:
                return (
                    <TaskItemPicker
                        key={wizardSteps[wizardStep].title}
                        connectors={connectors}
                        authenticatedConnectorsIds={
                            authenticationStatus &&
                            getAuthenticatedConnectors(connectors, 'action', authenticationStatus)
                        }
                        pickerItems={Object.values(connectors).filter(
                            (c) => Object.values(c.actions).length,
                        )}
                        isMultiSelect={true}
                        selectedItems={actionConnectors}
                        onSelect={(connector) => onActionConnectorSelected(connector as Connector)}
                        language={language}
                    />
                );
            case 3:
                return (
                    <TaskItemPicker
                        key={wizardSteps[wizardStep].title}
                        connectors={connectors}
                        pickerItems={
                            actionConnectors.length
                                ? actionConnectors.flatMap((ac) => Object.values(ac.actions))
                                : []
                        }
                        isMultiSelect={true}
                        selectedItems={taskActions}
                        onSelect={(selected) => onActionSelected(selected as Action)}
                        language={language}
                    />
                );
            // default is also the first step
            default:
                return (
                    <TaskItemPicker
                        key={wizardSteps[wizardStep].title}
                        connectors={connectors}
                        authenticatedConnectorsIds={
                            authenticationStatus &&
                            getAuthenticatedConnectors(connectors, 'trigger', authenticationStatus)
                        }
                        pickerItems={Object.values(connectors).filter(
                            (c) => Object.values(c.triggers).length,
                        )}
                        isMultiSelect={false}
                        selectedItems={triggerConnector ? [triggerConnector] : []}
                        onSelect={(connector) => {
                            setTriggerConnector(connector as Connector);
                            // If we go back and select a different connector,
                            // clear any selected trigger events from the previous one.
                            if (connector.id !== taskTrigger?.providerId) {
                                setTaskTrigger(null);
                            }
                        }}
                        language={language}
                    />
                );
        }
    };

    return (
        <div className="new-task-page page-layout">
            {/* WIZARD */}
            {isLoadingAuthenticationStatus ? (
                <div className="new-task-loader" data-testid="authStatusLoading">
                    <Loading message={translations[language].COMMON_loading} fullScreen={true} />
                </div>
            ) : (
                <div className="new-task-wizard">
                    <header className="wizard-header" data-testid="wizardHeader">
                        <h3 className="step-title">{wizardSteps[wizardStep].title}</h3>
                    </header>
                    <section className="wizard-stepper" data-testid="wizardStepper">
                        {wizardSteps.map((step, index) => (
                            <React.Fragment key={step.title}>
                                <ExTooltip
                                    // Wrapping into ExTooltip so we have more control over tooltips
                                    // and can show the tooltips even if button is disabled.
                                    tooltipDelay={0}
                                    position={TooltipPosition.BOTTOM}
                                    alignment={TooltipAlignment.MIDDLE}
                                >
                                    <div className="step-tooltip-text">{step.title}</div>
                                    <ExIconButton
                                        data-testid={`stepButton${index + 1}`}
                                        slot="anchor"
                                        className="step-button"
                                        // For some reason Success icon is always slightly bigger than
                                        // Radio in IconButton(s).
                                        icon={`${step.isDone ? 'Success' : wizardStep === index ? 'Radio selected' : 'Radio empty'}`}
                                        circular={true}
                                        flavor={
                                            wizardStep === index
                                                ? IconButtonFlavor.BASE
                                                : IconButtonFlavor.BRANDED
                                        }
                                        size={IconButtonSize.DEFAULT}
                                        type={IconButtonType.TERTIARY}
                                        onClick={() => setWizardStep(index)}
                                        hideBrowserTooltip
                                        // Disable if this and previous step is not done.
                                        disabled={
                                            index > 0 &&
                                            !wizardSteps[index].isDone &&
                                            !wizardSteps[index - 1].isDone
                                        }
                                    />
                                </ExTooltip>
                                {index < wizardLastStep && <div className="step-dash" />}
                            </React.Fragment>
                        ))}
                    </section>
                    <section className="wizard-content ta-tile" data-testid="wizardContent">
                        {/* STEPS */}
                        <section
                            className="wizard-step"
                            data-testid={`wizardStep${wizardStep + 1}`}
                        >
                            {renderSteps()}
                        </section>
                        {/* TASK PREVIEW */}
                        <section className="task-preview" data-testid="taskPreview">
                            <p className="preview-title text md">
                                {translations[language].TASK_BUILDER_selected_options}
                            </p>
                            <div className="task-example">
                                {!taskTrigger && !taskActions.length && (
                                    <p className="task-hint text sm">
                                        {translations[language].TASK_BUILDER_selection_hint}
                                    </p>
                                )}
                                {!!taskTrigger && (
                                    <div className="example-trigger">
                                        <IconCircle
                                            connector={connectors[taskTrigger.providerId]}
                                        />
                                        <span className="text sm">{taskTrigger.name}</span>
                                    </div>
                                )}
                                {!!taskTrigger && (
                                    <div className="example-separator">
                                        <ExIcon
                                            className="separator-icon-right"
                                            icon="Right arrow"
                                            hide-browser-tooltip
                                        />
                                        <ExIcon
                                            className="separator-icon-down"
                                            icon="Down arrow"
                                            hide-browser-tooltip
                                        />
                                    </div>
                                )}
                                {!!taskActions.length && (
                                    <ul className="example-actions-list">
                                        {taskActions.map((action) => (
                                            <li key={action.id} className="example-action">
                                                <IconCircle
                                                    connector={connectors[action.providerId]}
                                                />
                                                <span className="text sm">{action.name}</span>
                                                <span className="action-plus">+</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </section>
                    </section>
                    {/* WIZARD FOOTER */}
                    <footer className="wizard-footer">
                        <ExButton
                            className="exo-button"
                            flavor={ButtonFlavor.BASE}
                            size={ButtonSize.LARGE}
                            type={ButtonType.SECONDARY}
                            disabled={wizardStep === 0}
                            onClick={() => wizardStep > 0 && setWizardStep(wizardStep - 1)}
                        >
                            {translations[language].COMMON_back}
                        </ExButton>
                        {(wizardStep < wizardLastStep || !taskTrigger || !taskActions.length) && (
                            <ExButton
                                className="exo-button"
                                flavor={ButtonFlavor.BASE}
                                size={ButtonSize.LARGE}
                                type={ButtonType.PRIMARY}
                                disabled={
                                    // at step 1 and no trigger connector was yet selected
                                    (wizardStep === 0 && !triggerConnector) ||
                                    // at step 2 and no trigger was yet selected
                                    (wizardStep === 1 && !taskTrigger) ||
                                    // at step 3 and no action connectors were selected
                                    (wizardStep === 2 && !actionConnectors.length) ||
                                    // at last step
                                    wizardStep === wizardLastStep
                                }
                                onClick={() =>
                                    wizardStep < wizardLastStep && setWizardStep(wizardStep + 1)
                                }
                            >
                                {translations[language].COMMON_next}
                            </ExButton>
                        )}
                        {wizardStep === wizardLastStep && !!taskTrigger && !!taskActions.length && (
                            <ExButton
                                className="exo-button configure-button"
                                flavor={ButtonFlavor.BASE}
                                size={ButtonSize.LARGE}
                                type={ButtonType.PRIMARY}
                                disabled={!(taskTrigger && taskActions.length)}
                                onClick={() =>
                                    taskTrigger &&
                                    taskActions.length &&
                                    onCreateTaskFromSelection(taskTrigger, taskActions)
                                }
                            >
                                {translations[language].TASK_BUILDER_create}
                                &nbsp;
                                <ExIcon
                                    className="btn-icon"
                                    icon="Right arrow"
                                    hide-browser-tooltip
                                />
                            </ExButton>
                        )}
                    </footer>
                </div>
            )}
        </div>
    );
};

export default NewTaskBuilder;
